import React, { useState, useEffect } from "react";
import { Button, Input, message, Upload, DatePicker, Rate } from "antd";
import { MdOutlineNavigateNext } from "react-icons/md";
import { PlusOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import axios from "axios";
import moment from "moment";
import "react-quill/dist/quill.snow.css";
import { useDrawer } from "../../../../context/drawer";

export default function UpdateReview() {
  const [loading, setLoading] = useState(false);
  const { drawerData, toggleDrawer } = useDrawer();

  const [formData, setFormData] = useState({
    name: "",
    review_date: null,
    rating: 0,
    content: "",
  });

  const [fileList, setFileList] = useState([]);
  const [initialImageExists, setInitialImageExists] = useState(false);

  // Prefill data
  useEffect(() => {
    if (drawerData?.data) {
      const review = drawerData.data;

      setFormData({
        name: review.name || "",
        review_date: review.review_date
          ? moment(review.review_date)
          : null,
        rating: Number(review.rating) || 0,
        content: review.content || "",
      });

      if (review.image) {
        let imageUrl = review.image;
        if (imageUrl.startsWith("/9j/") && !imageUrl.startsWith("data:image")) {
          imageUrl = `data:image/jpeg;base64,${imageUrl}`;
        }

        setFileList([
          {
            uid: review.id || "-current",
            name: "current_image.jpg",
            status: "done",
            url: imageUrl,
          },
        ]);
        setInitialImageExists(true);
      } else {
        setFileList([]);
        setInitialImageExists(false);
      }
    }

    return () => {
      fileList.forEach((file) => {
        if (file.url?.startsWith("blob:")) {
          URL.revokeObjectURL(file.url);
        }
      });
    };
  }, [drawerData.data]);

  const handleSubmitClick = async () => {
    const { name, review_date, rating, content } = formData;
    const reviewId = drawerData?.data?.id;

    if (!name || !review_date || !rating || !content) {
      message.warning("Please fill all required fields.");
      return;
    }

    if (!initialImageExists && fileList.length === 0) {
      message.warning("Please upload an image.");
      return;
    }

    try {
      setLoading(true);
      const data = new FormData();

      data.append("action", "update_review");
      data.append("id", reviewId);
      data.append("name", name);
      data.append("rating", rating);
      data.append("review_date", review_date.format("YYYY-MM-DD"));
      data.append("content", content);

      if (fileList.length > 0 && fileList[0].originFileObj) {
        data.append("image", fileList[0].originFileObj);
        data.append("image_updated", "true");
      } else if (initialImageExists && fileList.length === 0) {
        data.append("remove_image", "true");
        data.append("image_updated", "true");
      } else {
        data.append("image_updated", "false");
      }

      const resp = await axios.post(
        "https://skdm.in/server/sdg/reviews.php",
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (resp?.data?.status === "success" || resp.status === 200) {
        message.success("Review updated successfully!");
        toggleDrawer();
        drawerData?.onSuccess?.();
      } else {
        message.error(resp?.data?.message || "Failed to update review.");
      }
    } catch (err) {
      message.error(`Update failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-review-form">
      <label>Name</label>
      <Input
        value={formData.name}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, name: e.target.value }))
        }
        placeholder="Reviewer name"
      />

      <label className="mt-3">Review Date</label>
      <DatePicker
        className="w-100"
        value={formData.review_date}
        onChange={(date) =>
          setFormData((prev) => ({ ...prev, review_date: date }))
        }
      />

      <label className="mt-3">Rating</label>
      <Rate
        value={formData.rating}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, rating: value }))
        }
      />

      <label className="mt-3">Review Content</label>
      <ReactQuill
        value={formData.content}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, content: value }))
        }
        theme="snow"
        style={{ minHeight: 200 }}
      />

      <label className="mt-3 d-block">Image</label>
      <Upload
        listType="picture-card"
        maxCount={1}
        fileList={fileList}
        onRemove={() => setFileList([])}
        beforeUpload={(file) => {
          if (!file.type.startsWith("image/")) {
            message.error("Only image files allowed!");
            return Upload.LIST_IGNORE;
          }

          const newFile = {
            uid: Date.now().toString(),
            name: file.name,
            status: "done",
            originFileObj: file,
            url: URL.createObjectURL(file),
          };

          setFileList([newFile]);
          return false;
        }}
      >
        {fileList.length === 0 && (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        )}
      </Upload>

      <div className="buttonGroup w-100 d-flex justify-content-end mt-4">
        <Button
          className="otherButtons active"
          icon={<MdOutlineNavigateNext />}
          onClick={handleSubmitClick}
          loading={loading}
        >
          Update Review
        </Button>
      </div>
    </div>
  );
}
